import {
  type ComponentType,
  type FocusEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { useIsDesktop } from '@firsttable/web-theme';
import { CommandRoot, CommandEmpty, CommandInput, CommandList } from '@discover-ui/command';
import { PopoverRoot, PopoverTrigger, PopoverContent } from '@discover-ui/popover';
import { DialogTrigger } from '@discover-ui/dialog';
import Button from '@discover-ui/button';
import Chip from '@discover-ui/chip';
import CloseIcon from '@discover-ui/icons/close.svg';
import ChevronDownIcon from '@discover-ui/icons/chevron-down.svg';
import { cn } from '@discover-lib/utils';
import GetCitySuburbsQuery from '@discover-data/gql/queries/getCitySuburbs';
import CR257ChipMultiSelectModal from '@/components/analytics/experiments/CR257ImproveWebSuburbSelection/CR257ChipMultiSelectModal';
import CR257ChipOptionList from '@/components/analytics/experiments/CR257ImproveWebSuburbSelection/CR257ChipOptionList';

export type ChipMultiSelectComboBoxProps = {
  /** City to load suburbs for, fetched via the `getCitySuburbs` query. */
  cityId?: number | null;
  className?: string | null;
  error?: boolean;
  errorMessage?: string | null;
  id?: string;
  name?: string;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
  onOptionsAvailabilityChange?: (hasOptions: boolean) => void;
  value?: number[];
  onValueChange?: (value: number[]) => void;
};

type Suburb = { id: number | null; menuTitle?: string | null; title?: string | null };

type BaseRow = {
  key: string;
  label: string;
  /** 0 = top level, 1 = nested under a group header. */
  depth: number;
  /** Text cmdk filters against. */
  search: string;
};

/** A flattened suburb row. Groups render a muted header plus selectable location rows. */
type SelectableRow = BaseRow & {
  id: number;
  selectable: true;
};

type HeaderRow = BaseRow & {
  selectable: false;
};

type Row = SelectableRow | HeaderRow;

const MAX_SELECTIONS = 3;

/**
 * PD-5787/PD-5788: when a city has this many suburbs or fewer, skip the step and fall back to tagging the whole city instead of showing a near-empty picker.
 */
const SKIP_STEP_SUBURB_THRESHOLD = 3;

function getLabel(suburb: Suburb) {
  return suburb.menuTitle || suburb.title || 'Unnamed location';
}

type OptionGroup = {
  header: HeaderRow | null;
  items: SelectableRow[];
};

function buildSuburbRows(
  edges: Array<{
    node: Suburb & {
      suburbs?: { edges?: Array<{ node: Suburb }> | null } | null;
    };
  }>
): Row[] {
  const ungrouped: SelectableRow[] = [];
  const grouped: Row[] = [];

  edges.forEach(({ node }) => {
    const children = node.suburbs?.edges ?? [];
    const label = getLabel(node);

    if (children.length === 0) {
      if (node.id != null) {
        ungrouped.push({
          key: `${node.id}`,
          id: node.id,
          label,
          depth: 0,
          search: label,
          selectable: true,
        });
      }
      return;
    }

    const childRows: SelectableRow[] = children.flatMap(({ node: child }) => {
      if (child.id == null) return [];
      const childLabel = getLabel(child);
      return [
        {
          key: `${child.id}`,
          id: child.id,
          label: childLabel,
          depth: 1,
          search: childLabel,
          selectable: true,
        },
      ];
    });

    grouped.push({
      key: `header-${node.id ?? label}`,
      label,
      depth: 0,
      search: label,
      selectable: false,
    });

    if (node.id != null) {
      grouped.push({
        key: `${node.id}`,
        id: node.id,
        label: `All ${label}`,
        depth: 1,
        search: `All ${label}`,
        selectable: true,
      });
    }

    grouped.push(...childRows);
  });

  return [...ungrouped, ...grouped];
}

function groupSelectableRows(rows: Row[]): OptionGroup[] {
  const ungrouped: SelectableRow[] = [];
  const regionGroups: OptionGroup[] = [];

  for (const row of rows) {
    if (row.selectable === false) {
      regionGroups.push({ header: row, items: [] });
    } else if (row.depth === 0) {
      ungrouped.push(row);
    } else {
      const lastRegion = regionGroups[regionGroups.length - 1];
      if (lastRegion) {
        lastRegion.items.push(row);
      } else {
        ungrouped.push(row);
      }
    }
  }

  if (ungrouped.length === 0) {
    return regionGroups;
  }

  return [{ header: null, items: ungrouped }, ...regionGroups];
}

type ChipSelectTriggerProps = {
  Trigger: ComponentType<{ asChild?: boolean; children?: ReactNode }>;
  errorMessageId: string;
  errorText: string | null;
  hasError: boolean;
  hasPopup: 'dialog' | 'listbox';
  isOpen: boolean;
  loading: boolean;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
  onToggle: (id: number) => void;
  selectedOptions: Array<{ id: number; label: string }>;
  triggerId?: string;
  triggerPlaceholder: string;
};

function ChipSelectTrigger({
  Trigger,
  errorMessageId,
  errorText,
  hasError,
  hasPopup,
  isOpen,
  loading,
  onBlur,
  onToggle,
  selectedOptions,
  triggerId,
  triggerPlaceholder,
}: ChipSelectTriggerProps) {
  return (
    <div className="relative">
      <Trigger asChild>
        <Button
          kind="no-style"
          type="button"
          id={triggerId}
          aria-label={
            selectedOptions.length > 0
              ? `Selected suburbs: ${selectedOptions.map(({ label }) => label).join(', ')}`
              : 'Select suburbs'
          }
          aria-haspopup={hasPopup}
          aria-expanded={isOpen}
          aria-invalid={hasError || undefined}
          aria-describedby={errorText ? errorMessageId : undefined}
          aria-busy={loading}
          disabled={loading}
          onBlur={onBlur}
          className="peer absolute inset-0 rounded-sm outline-none disabled:cursor-not-allowed"
        />
      </Trigger>
      <div
        className={cn(
          'pointer-events-none relative flex min-h-14 w-full items-center justify-between gap-3 rounded-sm border bg-background px-3 py-2 text-left text-base transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-disabled:opacity-70',
          hasError
            ? 'border-coral text-coral peer-hover:border-coral peer-focus-visible:ring-coral'
            : 'border-input peer-hover:border-gray-300 peer-focus-visible:ring-ring'
        )}>
        <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
          {selectedOptions.length > 0 ? (
            selectedOptions.map(({ id, label }) => (
              <Chip
                key={id}
                className="rounded-full bg-ds-gray-light-25 p-2 text-xs font-medium text-foreground">
                <span className="ml-0.5 max-w-40 truncate">{label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  className="pointer-events-auto ml-0.5 inline-flex items-center justify-center rounded-full p-0.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggle(id);
                  }}>
                  <CloseIcon className="size-3" />
                </button>
              </Chip>
            ))
          ) : (
            <span className={cn('truncate', hasError ? 'text-coral' : 'text-muted-foreground')}>
              {triggerPlaceholder}
            </span>
          )}
        </span>
        <ChevronDownIcon
          className={cn('size-4 shrink-0', hasError ? 'text-coral' : 'text-gray-400')}
        />
      </div>
    </div>
  );
}

export default function ChipMultiSelectComboBox({
  cityId,
  className,
  error: hasError = false,
  errorMessage,
  id: triggerId,
  name,
  onBlur,
  onOptionsAvailabilityChange,
  value,
  onValueChange,
}: ChipMultiSelectComboBoxProps) {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const [uncontrolledSelected, setUncontrolledSelected] = useState<number[]>([]);
  const errorMessageId = useId();
  const previousCityIdRef = useRef(cityId);
  const isControlled = value !== undefined;
  const selected = isControlled ? value : uncontrolledSelected;
  const errorText = hasError ? errorMessage : null;
  const hasCityId = cityId != null;

  const setSelected = useCallback(
    (nextSelected: number[]) => {
      if (!isControlled) {
        setUncontrolledSelected(nextSelected);
      }
      onValueChange?.(nextSelected);
    },
    [isControlled, onValueChange]
  );

  const toggle = useCallback(
    (id: number) => {
      if (selected.includes(id)) {
        setSelected(selected.filter((current) => current !== id));
        return;
      }
      if (selected.length >= MAX_SELECTIONS) return;

      setSelected([...selected, id]);
    },
    [selected, setSelected]
  );

  useEffect(() => {
    if (previousCityIdRef.current !== cityId) {
      setSelected([]);
      previousCityIdRef.current = cityId;
    }
  }, [cityId, setSelected]);

  const {
    data: citySuburbsData,
    loading,
    error,
  } = useQuery(GetCitySuburbsQuery, {
    variables: { id: hasCityId ? cityId : 0 },
    skip: !hasCityId,
  });

  const edges = error ? [] : (citySuburbsData?.City?.suburbs?.edges ?? []);

  const rows: Row[] = loading ? [] : buildSuburbRows(edges);

  // Count the city's real suburbs only: "All <region>" rollups and group headers are not additional suburbs, so they must not push a small city over the skip threshold (PD-5787/PD-5788).
  const suburbCount = edges.reduce((total, { node }) => {
    const children = node.suburbs?.edges ?? [];
    if (children.length === 0) {
      return total + (node.id == null ? 0 : 1);
    }
    return total + children.filter(({ node: child }) => child.id != null).length;
  }, 0);
  const hasEnoughSuburbs = suburbCount > SKIP_STEP_SUBURB_THRESHOLD;

  useEffect(() => {
    if (!loading) {
      onOptionsAvailabilityChange?.(hasCityId && !error && hasEnoughSuburbs);
    }
  }, [error, hasCityId, hasEnoughSuburbs, loading, onOptionsAvailabilityChange]);

  if (!hasCityId || error || (!loading && !hasEnoughSuburbs)) {
    return null;
  }

  const labelById = new Map(
    rows.flatMap((row) => (row.selectable ? [[row.id, row.label] as const] : []))
  );

  const selectedSet = new Set(selected);
  const selectedOptions = selected.flatMap((id) => {
    const label = labelById.get(id);
    return label ? [{ id, label }] : [];
  });
  const isAtMax = selected.length >= MAX_SELECTIONS;
  const hasMaxSelections = selected.length === MAX_SELECTIONS;
  const isPickerOpen = !loading && open;
  const triggerPlaceholder = loading ? 'Loading…' : 'Select suburbs';
  const scrollTargetProps = name ? { name } : {};
  const optionGroups = groupSelectableRows(rows);
  const trigger = (
    <ChipSelectTrigger
      Trigger={isDesktop ? PopoverTrigger : DialogTrigger}
      errorMessageId={errorMessageId}
      errorText={errorText}
      hasError={hasError}
      hasPopup={isDesktop ? 'listbox' : 'dialog'}
      isOpen={isPickerOpen}
      loading={loading}
      onBlur={onBlur}
      onToggle={toggle}
      selectedOptions={selectedOptions}
      triggerId={triggerId}
      triggerPlaceholder={triggerPlaceholder}
    />
  );

  return (
    <div {...scrollTargetProps} className={cn('flex w-full scroll-mt-6 flex-col gap-3', className)}>
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="m-0 font-sans text-base font-normal">Favourite locations</h3>
          <Chip
            className={cn(
              'shrink-0 rounded-full px-2.5 py-1 text-[12px] font-medium',
              hasMaxSelections ? 'bg-ds-brand-50 text-ds-brand-900' : 'bg-gray-100 text-gray-500'
            )}>
            {selected.length}/{MAX_SELECTIONS} selected
          </Chip>
        </div>
        <p className="m-0 text-sm leading-snug text-muted-foreground">
          We&apos;ll use it to make sure our restaurant picks are relevant to you!
        </p>
      </div>

      <div>
        {isDesktop ? (
          <PopoverRoot open={isPickerOpen} onOpenChange={setOpen}>
            {trigger}
            <PopoverContent
              align="start"
              className="z-[1000] w-[var(--radix-popover-trigger-width)] min-w-72 p-0">
              <CommandRoot>
                <CommandInput placeholder="Search suburbs" rootClassName="my-3" />
                <CommandList className="px-3 pb-3">
                  <CommandEmpty>No locations found.</CommandEmpty>
                  <CR257ChipOptionList
                    groups={optionGroups}
                    selectedIds={selectedSet}
                    isAtMax={isAtMax}
                    onToggle={toggle}
                  />
                </CommandList>
              </CommandRoot>
            </PopoverContent>
          </PopoverRoot>
        ) : (
          <CR257ChipMultiSelectModal
            groups={optionGroups}
            isAtMax={isAtMax}
            maxSelections={MAX_SELECTIONS}
            onOpenChange={setOpen}
            onToggle={toggle}
            open={isPickerOpen}
            selectedIds={selectedSet}
            selectedOptions={selectedOptions}>
            {trigger}
          </CR257ChipMultiSelectModal>
        )}

        {errorText && (
          <p id={errorMessageId} className="mb-0 mt-1 text-sm text-coral" role="alert">
            {errorText}
          </p>
        )}
      </div>
    </div>
  );
}
